<script setup>

/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { defineProps, defineEmits, computed } from 'vue';

/*==================================================================*/
/*================== PROPS =========================================*/
/*==================================================================*/

const props = defineProps({
    paginaAtual: {
        type: Number,
        default: 1
    },
    totalPaginas: {
        type: Number,
        default: 1
    }
});

const emits = defineEmits([
    'update:pagina-atual',
]);

/*==================================================================*/
/*================== PAGINAS ========================================*/
/*==================================================================*/

const TOTAL_SLOTS = 7;

const paginas = computed(() => {

    const { paginaAtual, totalPaginas } = props;

    if(totalPaginas <= TOTAL_SLOTS){
        return Array.from({ length: totalPaginas }, (_, indice) => indice + 1);
    }

    const pertoDoInicio = paginaAtual <= 4;
    const pertoDoFim = paginaAtual >= totalPaginas - 3;

    if(pertoDoInicio){
        return [1, 2, 3, 4, 5, 'reticencias-fim', totalPaginas];
    }

    if(pertoDoFim){
        return [1, 'reticencias-inicio', totalPaginas - 4, totalPaginas - 3, totalPaginas - 2, totalPaginas - 1, totalPaginas];
    }

    return [1, 'reticencias-inicio', paginaAtual - 1, paginaAtual, paginaAtual + 1, 'reticencias-fim', totalPaginas];

});

/*==================================================================*/
/*================== NAVEGACAO ======================================*/
/*==================================================================*/

const irParaPagina = (pagina = 1) => {

    if(pagina < 1 || pagina > props.totalPaginas || pagina === props.paginaAtual){
        return;
    }

    emits('update:pagina-atual', pagina);

};

</script>

<template>

    <div class="c-tabela__footer">

        <div class="c-footer__texto">
            Mostrando página {{ props.paginaAtual }} de {{ props.totalPaginas }}
        </div>

        <div class="c-footer__paginacao">

            <button
                type="button"
                class="c-botao-pagina c-botao-pagina--texto"
                :disabled="props.paginaAtual <= 1"
                @click="irParaPagina(props.paginaAtual - 1)"
            >
                Anterior
            </button>

            <template v-for="pagina in paginas">

                <span
                    v-if="typeof pagina === 'string'"
                    :key="pagina"
                    class="c-botao-pagina c-botao-pagina--reticencias"
                >
                    ...
                </span>

                <button
                    v-else
                    type="button"
                    :key="pagina"
                    class="c-botao-pagina"
                    :class="{ 'c-botao-pagina--ativa': pagina === props.paginaAtual }"
                    @click="irParaPagina(pagina)"
                >
                    {{ pagina }}
                </button>

            </template>

            <button
                type="button"
                class="c-botao-pagina c-botao-pagina--texto"
                :disabled="props.paginaAtual >= props.totalPaginas"
                @click="irParaPagina(props.paginaAtual + 1)"
            >
                Próximo
            </button>

        </div>

    </div>

</template>

<style scoped>

/*==================================================================*/
/*================== Footer da Tabela ==============================*/
/*==================================================================*/

.c-tabela__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px;
    border-top: 1px solid #D1D1D1;
    background-color: #ffffff;
}

.c-footer__texto {
    font-size: 0.875rem;
    color: #666666;
}

.c-footer__paginacao {
    display: flex;
    align-items: center;
    gap: 6px;
}

/*==================================================================*/
/*================== Botao de Pagina ================================*/
/*==================================================================*/

.c-botao-pagina {
    inline-size: 36px;
    block-size: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #D1D1D1;
    border-radius: 4px;
    background-color: #ffffff;
    color: #333333;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.c-botao-pagina:hover:not(:disabled):not(.c-botao-pagina--ativa) {
    background-color: #F2F2F2;
}

.c-botao-pagina:disabled {
    color: #B0B0B0;
    cursor: not-allowed;
}

.c-botao-pagina--texto {
    inline-size: auto;
    padding-inline: 12px;
    color: #230bc1;
}

.c-botao-pagina--texto:disabled {
    color: #B0B0B0;
}

.c-botao-pagina--ativa {
    background-color: #230bc1;
    border-color: #230bc1;
    color: #ffffff;
}

.c-botao-pagina--reticencias {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-color: transparent;
    color: #999999;
    cursor: default;
}

</style>
